<template>
  <div>
    <div class="harbor-shell">
      <!-- Conversation rail -->
      <aside class="harbor-rail">
        <div class="harbor-rail__header">
          <div class="harbor-rail__title-row">
            <h1 class="harbor-rail__title">Messages</h1>
            <div class="flex align-items-center gap-x-xxs">
              <button v-if="!editMode"
                      type="button"
                      class="btn btn-link btn-compact"
                      data-cy="rail-edit"
                      aria-label="Edit conversations"
                      @click="editMode = true">
                Edit
              </button>
              <button v-if="!editMode"
                      type="button"
                      class="harbor-rail__new-btn"
                      data-cy="rail-new"
                      @click="newMessage()">
                <i class="fa-regular fa-pen-to-square" aria-hidden="true"></i>
                <span class="hidden-xs">New</span>
              </button>
              <button v-else
                      type="button"
                      class="btn btn-danger btn-compact"
                      :disabled="hiding"
                      @click="editMode = false">
                Cancel
              </button>
            </div>
          </div>
          <div class="harbor-rail__search">
            <i class="fa-solid fa-magnifying-glass harbor-rail__search-icon" aria-hidden="true"></i>
            <input v-model="searchQuery" data-cy="rail-search" placeholder="Search messages" />
          </div>
          <p v-if="showMinCharsHint" class="m-t-xs m-b-none text-12 text-gray-500">
            Type at least 2 characters to search
          </p>
        </div>

        <!-- Unified search results -->
        <div v-if="isSearching" class="harbor-rail__list">
          <div class="p-x-m p-y-xs">
            <small v-if="searchLoading">Searching for <span class="text-blue-600">{{ searchQuery }}</span></small>
            <small v-else-if="searchCompleted">{{ searchResults.length }} {{ searchResults.length === 1 ? 'result' : 'results' }} for <span class="text-blue-600">{{ searchQuery }}</span></small>
          </div>
          <message-search-results
            :results="searchResults"
            :loading="searchLoading"
            :searched="searchCompleted"
            :query="searchQuery"
          />
        </div>

        <!-- Normal groups list -->
        <div v-else class="harbor-rail__list" ref="listEl" @scroll="handleListScroll">
          <div v-if="groupsLoading" class="p-m text-center">
            <spinner :inline="true"></spinner>
          </div>
          <template v-else>
            <div v-if="messageGroups.length === 0" class="harbor-rail__empty">
              No conversations yet.
            </div>

            <template v-else>
              <div v-if="pinnedGroups.length > 0" class="harbor-rail__section-label">Pinned</div>
              <conversation-list-item
                v-for="group in pinnedGroups"
                :key="`p-${group.id}`"
                :group="group"
                :profile-id="profileId"
                :profile-type="profileType"
                :edit-mode="editMode"
                :selected="isSelected(group)"
                :hover="shouldShowHoverMenuFor(group)"
                :updating="hoverGroupUpdating(group)"
                @mouseenter.native="showHoverMenuFor(group)"
                @mouseleave.native="hideHoverMenuFor(group)"
                @click="handleTap"
                @toggle-select="toggleSelect"
                @pin-toggle="togglePin"
              />

              <div v-if="unpinnedGroups.length > 0 && pinnedGroups.length > 0" class="harbor-rail__section-label">Recent</div>
              <conversation-list-item
                v-for="group in unpinnedGroups"
                :key="`r-${group.id}`"
                :group="group"
                :profile-id="profileId"
                :profile-type="profileType"
                :edit-mode="editMode"
                :selected="isSelected(group)"
                :hover="shouldShowHoverMenuFor(group)"
                :updating="hoverGroupUpdating(group)"
                @mouseenter.native="showHoverMenuFor(group)"
                @mouseleave.native="hideHoverMenuFor(group)"
                @click="handleTap"
                @toggle-select="toggleSelect"
                @pin-toggle="togglePin"
              />
            </template>

            <div v-if="groupsLoadingMore" class="p-m text-center">
              <spinner :inline="true"></spinner>
            </div>
            <div v-if="groupsNoMoreData && messageGroups.length > 0" class="harbor-rail__footer">
              All conversations shown
            </div>
          </template>
        </div>
      </aside>

      <!-- Empty state (desktop right pane) -->
      <section class="harbor-main">
        <div class="harbor-empty">
          <div class="harbor-empty__icon">
            <i class="fa-regular fa-comments" aria-hidden="true"></i>
          </div>
          <p class="harbor-empty__title">Select a conversation</p>
          <p class="harbor-empty__body">Choose a conversation from the list to view messages, or start a new one.</p>
        </div>
      </section>
    </div>

    <div class="floating-footer-toolbar" v-if="editMode">
      <div class="flex align-items-center justify-space-between">
        <div>
          <div class="flex align-items-center">
            <p class="text-gray-50 text-16 font-heavy m-b-none m-r-xs">
              {{ selectedGroups.length }} selected
            </p>
            <spinner v-if="hiding"></spinner>
          </div>
          <p class="text-red-50" v-if="error">
            {{ errorMessage }}
          </p>
        </div>
        <div>
          <button class="btn btn-primary" data-cy="rail-hide" v-on:click="hideSelected()" :disabled="hiding">Hide</button>
        </div>
        <div>
          <button class="btn btn-default" v-on:click="editMode = false" :disabled="hiding">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from './spinner.vue';
import MessageSearchResults from './message_search_results.vue';
import ConversationListItem from './conversation_list_item.vue';
import { paramAndHistoryMixin } from './mixins/parameter_history_mix';

export default {
  name: 'messaging',
  components: {
    Spinner,
    MessageSearchResults,
    ConversationListItem,
  },
  mixins: [paramAndHistoryMixin],
  data() {
    return {
      // Search state
      searchQuery: '',
      searchResults: [],
      searchLoading: false,
      searchCompleted: false,
      showMinCharsHint: false,
      minCharsTimer: null,

      // Groups list state
      messageGroups: [],
      groupsLoading: false,
      groupsLoadingMore: false,
      groupsNoMoreData: false,
      groupsNextPageUrl: null,

      // Edit mode state
      editMode: false,
      selectedGroups: [],
      hiding: false,
      error: false,
      errorMessage: 'Hiding failed. Please try again or contact support@wrestlingiq.com for assistance.',
      hoverMenuGroups: {},
    };
  },
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    profileId: {
      type: Number,
      required: true,
    },
    profileType: {
      type: String,
      required: true,
    },
  },
  computed: {
    isSearching() {
      return this.searchQuery.length >= 2;
    },
    pinnedGroups() {
      return this.messageGroups.filter((g) => {
        const m = this.membershipFor(g);
        return m && m.pinned;
      });
    },
    unpinnedGroups() {
      return this.messageGroups.filter((g) => {
        const m = this.membershipFor(g);
        return !m || !m.pinned;
      });
    },
  },
  created() {
    this.debouncedSearch = _.debounce(this.performSearch, 400);

    // Restore search query from URL for back-button support
    const savedQuery = this.getQueryParameter('q') || '';
    if (savedQuery.length >= 2) {
      this.searchQuery = savedQuery;
    }

    this.loadGroups();
  },
  destroyed() {
    if (this.minCharsTimer) clearTimeout(this.minCharsTimer);
  },
  watch: {
    searchQuery(newVal) {
      if (this.minCharsTimer) clearTimeout(this.minCharsTimer);
      this.showMinCharsHint = false;

      if (newVal.length >= 2) {
        this.searchLoading = true;
        this.searchCompleted = false;
        this.debouncedSearch();
        this.replaceHistoryState('q', newVal);
      } else if (newVal.length > 0) {
        this.searchResults = [];
        this.searchLoading = false;
        this.searchCompleted = false;
        this.replaceHistoryState('q', null);
        this.minCharsTimer = setTimeout(() => {
          if (this.searchQuery.length > 0 && this.searchQuery.length < 2) {
            this.showMinCharsHint = true;
          }
        }, 1000);
      } else {
        this.searchResults = [];
        this.searchLoading = false;
        this.searchCompleted = false;
        this.replaceHistoryState('q', null);
      }
    },
  },
  methods: {
    // --- Search ---
    performSearch() {
      const query = this.searchQuery.trim();
      if (query.length < 2) return;

      this.searchLoading = true;
      axios.get(this.$apiService.messageSearchUrl(), { params: { q: query, per_page: 30 } })
        .then((response) => {
          this.searchResults = response.data.results;
          this.searchCompleted = true;
          this.searchLoading = false;
        })
        .catch(() => {
          this.searchResults = [];
          this.searchCompleted = true;
          this.searchLoading = false;
        });
    },

    // --- Groups list ---
    loadGroups() {
      this.groupsLoading = true;
      axios.get(this.$apiService.messageGroupsV2Url(), { params: { query: '', page: 1, per_page: 30 } })
        .then((response) => {
          this.messageGroups = response.data.message_groups;
          this.setupGroupsPagination(response.headers);
          this.groupsLoading = false;
        })
        .catch(() => {
          this.groupsLoading = false;
        });
    },
    loadMoreGroups() {
      if (this.groupsLoadingMore || !this.groupsNextPageUrl) return;

      this.groupsLoadingMore = true;
      axios.get(this.groupsNextPageUrl)
        .then((response) => {
          this.messageGroups = this.messageGroups.concat(response.data.message_groups);
          this.setupGroupsPagination(response.headers);
          this.groupsLoadingMore = false;
        })
        .catch(() => {
          this.groupsLoadingMore = false;
        });
    },
    setupGroupsPagination(headers) {
      this.groupsNextPageUrl = null;
      this.groupsNoMoreData = false;

      if (!headers.link) {
        this.groupsNoMoreData = this.messageGroups.length > 0;
        return;
      }

      const links = headers.link.split(',');
      links.forEach((l) => {
        if (l.indexOf('rel="next"') !== -1) {
          const match = /\<([^>]+)>/.exec(l);
          if (match) this.groupsNextPageUrl = match[1];
        }
      });

      if (!this.groupsNextPageUrl) {
        this.groupsNoMoreData = this.messageGroups.length > 0;
      }
    },
    handleListScroll() {
      if (this.isSearching || !this.groupsNextPageUrl) return;
      const el = this.$refs.listEl;
      if (!el) return;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        this.loadMoreGroups();
      }
    },

    // --- Group actions ---
    newMessage() {
      window.location.assign('/message_groups/new');
    },
    membershipFor(group) {
      return _.find(group.memberships, (membership) => membership.profile.profile_id === this.profileId && membership.profile.profile_type === this.profileType);
    },
    isSelected(group) {
      return this.selectedGroups.indexOf(group) !== -1;
    },
    toggleSelect(group) {
      const idx = this.selectedGroups.indexOf(group);
      if (idx !== -1) {
        this.selectedGroups.splice(idx, 1);
      } else {
        this.selectedGroups.push(group);
      }
    },
    getMenuGroupFor(group, setHoveringTo) {
      let hGroup = this.hoverMenuGroups[group.id];
      if (!hGroup) {
        const membership = this.membershipFor(group);
        hGroup = {
          pinned: membership ? membership.pinned : false,
          hovering: setHoveringTo,
          updating: false,
        };
      }
      hGroup.hovering = setHoveringTo;
      return hGroup;
    },
    hideHoverMenuFor(group) {
      const hGroup = this.getMenuGroupFor(group, false);
      this.$set(this.hoverMenuGroups, group.id, hGroup);
    },
    showHoverMenuFor(group) {
      const hGroup = this.getMenuGroupFor(group, true);
      this.$set(this.hoverMenuGroups, group.id, hGroup);
    },
    shouldShowHoverMenuFor(group) {
      const hGroup = this.hoverMenuGroups[group.id];
      return !!hGroup?.hovering;
    },
    hoverGroupUpdating(group) {
      const hGroup = this.hoverMenuGroups[group.id];
      return !!hGroup?.updating;
    },
    handleTap(event, group) {
      if (this.editMode) {
        event.preventDefault();
        this.toggleSelect(group);
      }
    },
    hideSelected() {
      this.hiding = true;
      this.error = false;
      const vm = this;
      const promises = [];
      this.selectedGroups.forEach(async (group) => {
        const membership = this.membershipFor(group);
        if (membership) {
          promises.push(this.hideGroupMembership(membership.id));
        }
      });

      Promise.all(promises)
        .then((results) => {
          vm.$notificationManager.emit('show-toast', 'Message groups hidden', true);
          window.location.reload();
        })
        .catch((error) => {
          vm.error = true;
        });
    },
    async hideGroupMembership(membershipId) {
      const params = {
        message_membership: {
          id: membershipId,
          group_visible: false,
        },
      };
      return axios.put(this.$apiService.messageMembershipUrl(membershipId), params)
        .then(() => {
          console.log(`Hid ${membershipId}`);
        })
        .catch(() => {
          console.error(`Could not hide ${membershipId}`);
        });
    },
    togglePin(group) {
      const membership = this.membershipFor(group);
      const hGroup = this.hoverMenuGroups[group.id] || this.getMenuGroupFor(group, false);
      if (hGroup?.updating) {
        return;
      }

      hGroup.updating = true;
      hGroup.pinned = !hGroup.pinned;
      this.$set(this.hoverMenuGroups, group.id, hGroup);

      if (membership) {
        const params = {
          message_membership: {
            id: membership.id,
            pinned: hGroup.pinned,
          },
        };
        axios.put(this.$apiService.messageMembershipUrl(membership.id), params)
          .then(() => {
            const pinnedLanguage = hGroup.pinned ? 'Pinned' : 'Unpinned';
            this.$notificationManager.emit('show-toast', `${pinnedLanguage} ${group.name}`, true);
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(() => {
            console.error(`Could not toggle pinned for ${membership.id}`);
            hGroup.updating = false;
            hGroup.pinned = !hGroup.pinned;
            this.$set(this.hoverMenuGroups, group.id, hGroup);
            this.$notificationManager.emit('show-toast', `Failed to toggle pin for ${group.name}`, false);
          });
      }
    },
  },
};
</script>
